import { CardCustomizerTelemetryProvider, CustomizationProvider, useCardCustomization, useCustomizationContext } from '@apps/card/routes/CardCustomizer';

import React, { useEffect } from 'react';
import { enrichCardCutomizerTelemetryExtraInfo } from '@apps/card/routes/CardCustomizer';
import { Code, Envelope, Invitation, MailingAddress, Share, Website, Edit, Photos, DesignEmail } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
// import { InfoTabs } from './InfoTabs';
import { CustomizeNowButton } from '../../CustomizeNowButton';
import type { DraftData, ThemeJson } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';
import { InlineValueProps, ListValueProps } from '../ValueProps/ValueProps';
import { useCustomizationPriceSummary } from '../../useCustomizationPriceSummary';
import { StationeryDraftFormat, StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import { CardFrontCustomizations } from '../../../../../PaperCardCustomizer/steps/CardDesign/PaperCardFrontConfigPanel';
import { BestDeliveryDayEstimation } from './BestDeliveryDayEsmation';
import { useCardCustomizerConfig } from '@apps/card/hooks/useCardCustomizedConfig';
import { getDimensions } from '@apps/card/utils';
import { FreeShippingInlineBanner } from './FreeShippingInlineBanner';
import { Flex, TextV2 } from '@withjoy/joykit';
import { MatchingFoilTemplate } from '../MatchingFoilTemplate';
import { useFreeShippingCountries } from '../../useFreeShippingCountries';
import { useStationeryProductPricingContext } from '@apps/card/components/ProductUIConfig/utils/useStationeryProductPricingContext';
import { CheckIcon } from '@assets/icons';
import { OrientationChoiceGroup } from '../OrientationChoiceGroup';
import { SizeChoiceGroup } from '../SizeChoiceGroup';
import { ColorwaySelector } from '../ColorwaySelector';
import { usePdpColorways } from '../../PdpColorwayContext';

interface CustomizationPaneProps {
  countriesWithFreeShipping?: string[];
  onCustomizeClick: () => void;
  isCreatingDraft: boolean;
  customizeButtonRef: React.RefObject<HTMLButtonElement>;
  hideCustomizeNowButton?: boolean;
  category: StationeryTemplateCategoryEnum;
  templateId: string;
  themeId: string;
}

const useInlineValuePropsByCategory = (category: StationeryTemplateCategoryEnum) => {
  const { t, tq } = useTranslation('stationery');
  switch (category) {
    case StationeryTemplateCategoryEnum.menu:
    case StationeryTemplateCategoryEnum.sign:
    case StationeryTemplateCategoryEnum.napkin: {
      return [];
    }
    case StationeryTemplateCategoryEnum.holiday: {
      return [
        {
          icon: <Envelope />,
          description: tq`Free Envelope Printing`,
        },
        {
          icon: <MailingAddress />,
          description: tq`Contact Collector!`,
        },
        {
          icon: <DesignEmail />,
          description: tq`Exclusive Designs`,
        },
      ];
    }
    default: {
      const trans = t('pdpModal', 'customizationPane', 'printTemplateConfig');
      const inlineValuePropsTrans = trans.inlineValueProps;
      return [
        {
          icon: <Website />,
          description: inlineValuePropsTrans.websiteDesign(),
        },
        {
          icon: <MailingAddress />,
          description: inlineValuePropsTrans.contactCollector(),
        },
        {
          icon: <Code />,
          description: inlineValuePropsTrans.qrCodes(),
        },
      ];
    }
  }
};

const Check = () => {
  return (
    <Flex
      width={6}
      height={6}
      padding={2}
      borderRadius="full"
      backgroundColor="mono2"
      justifyContent="center"
      alignItems="center"
    >
      <CheckIcon />
    </Flex>
  );
};

const useListValuePropsByCategory = (category: StationeryTemplateCategoryEnum) => {
  const { t, tq } = useTranslation('stationery');
  const trans = t('pdpModal', 'customizationPane', 'printTemplateConfig');
  const [paneCardDimensions] = useCustomizationContext(draft => getDimensions(draft.config.card));
  const { helpers } = useCardCustomizerConfig(StationeryDraftFormat.print, category, paneCardDimensions);

  const lisValuePropsTrans = trans.listValueProps;
  switch (category) {
    case StationeryTemplateCategoryEnum.holiday: {
      return {
        sectionTitle: lisValuePropsTrans.sectionTitle(),
        items: [
          {
            icon: <Invitation size={24} />,
            description: tq`Digital printing on our luxurious signature card stock with a subtle velvety texture`,
          },
          {
            icon: <Edit size={24} />,
            description: tq`Add rounded corners and customize your card the way you want`,
          },
          {
            icon: <Photos size={24} />,
            description: tq`Full color back of card designs with a variety of pattern and photo layouts`,
          },
          {
            icon: <Envelope size={24} />,
            description: tq`Premium white envelopes with complimentary address printing`,
          },
        ],
      };
    }
    case StationeryTemplateCategoryEnum.napkin: {
      return {
        items: [
          {
            icon: <Check />,
            description: (
              <>
                <TextV2
                  fontWeight={600}
                  as="span"
                >
                  {tq`Personalized Touch:`}
                </TextV2>{' '}
                {tq`Add a personalized touch to your cocktail bar or dinner table with custom napkins that showcase your unique style.`}
              </>
            ),
          },
          {
            icon: <Check />,
            description: (
              <>
                <TextV2
                  fontWeight={600}
                  as="span"
                >
                  {tq`Showcase Your Style:`}
                </TextV2>{' '}
                {tq`Choose a classic monogram, add a fun photo of your pet, or upload your own custom design.`}
              </>
            ),
          },
          {
            icon: <Check />,
            description: (
              <>
                <TextV2
                  fontWeight={600}
                  as="span"
                >
                  {tq`Multiple sizes:`}
                </TextV2>{' '}
                {tq`Available in Cocktail (4.75" square) and Luncheon (6.5" square) sizes.`}
              </>
            ),
          },
        ],
      };
    }
    case StationeryTemplateCategoryEnum.sign: {
      return {
        items: [
          {
            icon: <Check />,
            description: (
              <>
                <TextV2
                  fontWeight={600}
                  as="span"
                >
                  {tq`Match your wedding aesthetic:`}
                </TextV2>{' '}
                {tq`Create a cohesive and elegant look with custom signs designed to match your wedding's aesthetic. From classic to modern, we'll bring your vision to life.`}
              </>
            ),
          },
          {
            icon: <Check />,
            description: (
              <>
                <TextV2
                  fontWeight={600}
                  as="span"
                >
                  {tq`Lightweight and durable:`}
                </TextV2>{' '}
                {tq`Made from lightweight and durable 3/16" paper-faced foam board.`}
              </>
            ),
          },
          {
            icon: <Check />,
            description: (
              <>
                <TextV2
                  fontWeight={600}
                  as="span"
                >
                  {tq`Easel Display:`}
                </TextV2>{' '}
                {tq`Ideal for display on an easel or stand (not included).`}
              </>
            ),
          },
        ],
      };
    }
    case StationeryTemplateCategoryEnum.menu: {
      return {
        items: [
          {
            icon: <Check />,
            description: (
              <>
                <TextV2
                  fontWeight={600}
                  as="span"
                >
                  {tq`Personalized Touch:`}
                </TextV2>{' '}
                {tq`Delight your guests with custom menus that beautifully present your carefully selected meal or cocktail offerings.`}
              </>
            ),
          },
          {
            icon: <Check />,
            description: (
              <>
                <TextV2
                  fontWeight={600}
                  as="span"
                >
                  {tq`Showcase Your Style:`}
                </TextV2>{' '}
                {tq`Choose from whimsical illustrations, a classic monogram, or upload your own.`}
              </>
            ),
          },
          {
            icon: <Check />,
            description: (
              <>
                <TextV2
                  fontWeight={600}
                  as="span"
                >
                  {tq`Options Available:`}
                </TextV2>{' '}
                {tq`Digital printing on your choice of luxurious card stock.`}
              </>
            ),
          },
        ],
      };
    }
    default: {
      return {
        sectionTitle: lisValuePropsTrans.sectionTitle(),
        items: [
          {
            icon: <Website size={24} />,
            description: lisValuePropsTrans.paperType(),
          },
          {
            icon: <Invitation size={24} />,
            description: lisValuePropsTrans.silhouette(),
          },
          {
            icon: <Share size={24} />,
            description: lisValuePropsTrans.qrCodes(),
          },
          ...(helpers.isStepEnabled('envelope')
            ? [
                {
                  icon: <Envelope size={24} />,
                  description: lisValuePropsTrans.envelope(),
                },
              ]
            : []),
        ],
      };
    }
  }
};

const Root = (props: CustomizationPaneProps) => {
  const { category, customizeButtonRef, hideCustomizeNowButton, onCustomizeClick, isCreatingDraft, templateId, themeId } = props;

  const { options, selectedColorwayId, onColorwaySelect } = usePdpColorways();

  const { summary } = useCustomizationPriceSummary();

  enrichCardCutomizerTelemetryExtraInfo({
    stationeryDraftId: null,
    stationeryTemplateCategory: category,
    themeId,
    step: null,
    pageOverride: 'pdpModal',
    orderNumber: null,
    stationeryDraftFormat: null,
  });

  const listValueProps = useListValuePropsByCategory(category);
  const inlineValueProps = useInlineValuePropsByCategory(category);

  const valueProps = (
    <>
      {inlineValueProps.length > 0 && <InlineValueProps items={inlineValueProps} />}
      <ListValueProps
        marginTop={!inlineValueProps.length ? 7 : 0}
        items={listValueProps.items}
        sectionTitle={listValueProps.sectionTitle}
        marginBottom={0}
      />
    </>
  );

  const { activeProduct } = useStationeryProductPricingContext();
  const countriesWithFreeShipping = useFreeShippingCountries([activeProduct]);

  return (
    <>
      <CustomizeNowButton
        hide={hideCustomizeNowButton}
        ref={customizeButtonRef}
        isLoading={isCreatingDraft}
        onCustomizeClick={onCustomizeClick}
        marginTop={0}
        marginBottom={5}
      />
      {!hideCustomizeNowButton && (
        <Flex
          marginBottom={7}
          flexDirection="column"
          gap={3}
        >
          {countriesWithFreeShipping.length > 0 && <FreeShippingInlineBanner countriesWithFreeShipping={countriesWithFreeShipping} />}
          <BestDeliveryDayEstimation />
        </Flex>
      )}
      <Flex
        flexDirection="column"
        gap={7}
      >
        <ColorwaySelector
          options={options}
          selectedColorwayId={selectedColorwayId}
          onSelect={onColorwaySelect}
        />
        <OrientationChoiceGroup templateId={templateId} />
        <SizeChoiceGroup templateId={templateId} />
        <CardFrontCustomizations
          subtotal={summary.total}
          isInsidePDP={true}
        />
      </Flex>
      <CustomizeNowButton
        isLoading={isCreatingDraft}
        onCustomizeClick={onCustomizeClick}
        marginTop={7}
        marginBottom={5}
      />
      <Flex
        flexDirection="column"
        gap={3}
      >
        {countriesWithFreeShipping.length > 0 && <FreeShippingInlineBanner countriesWithFreeShipping={countriesWithFreeShipping} />}
        <BestDeliveryDayEstimation />
      </Flex>
      {valueProps}
      <MatchingFoilTemplate
        category={category}
        themeId={themeId}
      />
    </>
  );
};

interface CardCustomizationProps {
  countriesWithFreeShipping?: string[];
  intersectionRef: React.RefObject<HTMLButtonElement>;
  hideCustomizeNowButton: boolean;
  isCreatingDraft: boolean;
  onCardCustomizeClick: () => void;
  onCardCustomization: (draft: DraftData) => void;
  customizationConfig: ThemeJson;
  stationeryTemplateCategory: StationeryTemplateCategoryEnum;
  templateId: string;
  themeName: string;
  themeId: string;
}

export const CardCustomizationPane = (props: CardCustomizationProps) => {
  const {
    countriesWithFreeShipping,
    customizationConfig,
    hideCustomizeNowButton,
    intersectionRef,
    isCreatingDraft,
    onCardCustomizeClick,
    onCardCustomization,
    stationeryTemplateCategory,
    templateId,
    themeId,
    themeName,
  } = props;
  const { scope, state } = useCardCustomization({
    config: customizationConfig,
    template: {
      themeId,
      name: themeName,
    },
    stationeryTemplateCategory,
  });

  useEffect(() => {
    onCardCustomization(state);
  }, [state, onCardCustomization]);
  return (
    <CustomizationProvider value={scope}>
      <CardCustomizerTelemetryProvider>
        <Root
          countriesWithFreeShipping={countriesWithFreeShipping}
          hideCustomizeNowButton={hideCustomizeNowButton}
          customizeButtonRef={intersectionRef}
          themeId={themeId}
          templateId={templateId}
          category={stationeryTemplateCategory}
          isCreatingDraft={isCreatingDraft}
          onCustomizeClick={onCardCustomizeClick}
        />
      </CardCustomizerTelemetryProvider>
    </CustomizationProvider>
  );
};
